import React, { useMemo } from 'react';
import { Box, Hint, HintText, ShimmerBox, Text } from '@common-fe/common-fe';

import { EMPTY_FIELD_VALUE } from '@/common/constants';
import { AccentText } from '@/components';

import { StatisticCard } from '../../StatisticCard.styles';
import { SubstantiationRateStatisticProps } from '../SubstantiationRateStatistic.types';

import SubstantiationRateDropdown from './SubstantiationRateDropdown';
import { StyledTitle } from './SubstantiationRateStatistic.styles';

export const SubstantiationRateStatistic: React.FC<SubstantiationRateStatisticProps> = ({
  isLoading,
  percents,
  options,
  currentRange,
  onChangeRange,
}) => {
  const metricNode = useMemo(() => {
    if (isLoading) {
      return (
        <>
          <ShimmerBox
            data-testid="dashboard_statistics_substantiation-rate_loader"
            round="checkboxRound"
            width="77px"
            height="36px"
          />
          <ShimmerBox round="checkboxRound" width="100%" height="20px" margin={{ top: 'spacing2' }} />
        </>
      );
    }

    if (percents === null || percents === undefined) {
      return (
        <AccentText data-testid="dashboard_statistics_substantiation-rate_empty" size="2xl" weight={700}>
          {EMPTY_FIELD_VALUE}
        </AccentText>
      );
    }

    return (
      <>
        <AccentText data-testid="dashboard_statistics_substantiation-rate_percent" size="2xl" weight={700}>
          {`${percents}%`}
        </AccentText>
        <Text data-testid="dashboard_statistics_substantiation-rate_label" size="medium" weight={400}>
          Claims
        </Text>
      </>
    );
  }, [isLoading, percents]);

  return (
    <StatisticCard
      data-testid="dashboard_statistics_substantiation-rate"
      background="canvas"
      border={{ color: 'border2', size: '1px' }}
      round="container1Round"
      pad="spacing16"
      direction="column"
      gap="spacing12"
      height={{ min: '140px' }}
    >
      <Box direction="row" align="center" justify="between" height="20px">
        <Hint
          hintWidth={240}
          hintHeight={100}
          hintElement={
            <Box>
              <HintText>
                <StyledTitle
                  data-testid="dashboard_statistics_substantiation-rate_title"
                  size="medium"
                  weight={700}
                >
                  Substantiation Rate
                </StyledTitle>
              </HintText>
            </Box>
          }
        >
          <Text data-testid="dashboard_statistics_substantiation-rate_hint" textAlign="center" size="small">
            Percentage of card claims approved without manual verification
          </Text>
        </Hint>

        <SubstantiationRateDropdown value={currentRange} onChangeValue={onChangeRange} options={options} />
      </Box>

      <Box
        data-testid="dashboard_statistics_substantiation-rate_value"
        border={{ color: 'border2', size: '1px' }}
        round="container2Round"
        pad={{ horizontal: 'spacing12', vertical: 'spacing8' }}
        direction="column"
        justify="center"
        gap="spacing2"
      >
        {metricNode}
      </Box>
    </StatisticCard>
  );
};

export default SubstantiationRateStatistic;
